import { styled } from '@mui/material';
import Divider from 'shell/components/Divider';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';

const RichTextToolbar = ({ disabled, editor, ...rest }) => {
    const handleClick = (e, formatType) => {
        e.preventDefault();
        switch (formatType) {
            case 'bold':
                editor.chain().focus().toggleBold().run();
                break;
            case 'italic':
                editor.chain().focus().toggleItalic().run();
                break;
            case 'underline':
                editor.chain().focus().toggleUnderline().run();
                break;
            case 'listUnordered':
                editor.chain().focus().toggleBulletList().run();
                break;
            case 'listOrdered':
                editor.chain().focus().toggleOrderedList().run();
                break;
            case 'link':
                if (editor.isActive('link')) {
                    editor.chain().focus().unsetLink().run();
                } else {
                    const { view, state } = editor;
                    const { from, to } = view.state.selection;
                    const text = state.doc.textBetween(from, to, '');
                    editor
                        .chain()
                        .focus()
                        .extendMarkRange('link')
                        .setLink({
                            href: text.startsWith('http') ? text : `https://${text}`,
                            rel: 'noopener noreferrer nofollow',
                            target: '_blank',
                        })
                        .run();
                }
                break;
            case 'code':
                editor.chain().focus().toggleCode().run();
                break;
            default:
                break;
        }
    };

    return (
        <RootStack
            direction={'row'}
            disabled={disabled}
            divider={<Divider flexItem orientation={'vertical'} />}
            spacing={1}
            {...rest}
        >
            <Stack direction={'row'} spacing={1}>
                <CustomIconButton
                    aria-label={'apply bold'}
                    active={editor?.isActive('bold')}
                    disabled={disabled}
                    kind={'bold'}
                    size={'small'}
                    variant={'square'}
                    onMouseDown={(e) => handleClick(e, 'bold')}
                    onKeyPress={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                            handleClick(e, 'bold');
                        }
                    }}
                />
                <CustomIconButton
                    aria-label={'apply italic'}
                    active={editor?.isActive('italic')}
                    disabled={disabled}
                    kind={'italic'}
                    size={'small'}
                    variant={'square'}
                    onMouseDown={(e) => handleClick(e, 'italic')}
                    onKeyPress={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                            handleClick(e, 'italic');
                        }
                    }}
                />
                <CustomIconButton
                    aria-label={'apply underline'}
                    active={editor?.isActive('underline')}
                    disabled={disabled}
                    kind={'underline'}
                    size={'small'}
                    variant={'square'}
                    onMouseDown={(e) => handleClick(e, 'underline')}
                    onKeyPress={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                            handleClick(e, 'underline');
                        }
                    }}
                />
            </Stack>
            <Stack direction={'row'} spacing={1}>
                <CustomIconButton
                    aria-label={'apply unordered list'}
                    active={editor?.isActive('bulletList')}
                    disabled={disabled}
                    kind={'listUnordered'}
                    size={'small'}
                    variant={'square'}
                    onMouseDown={(e) => handleClick(e, 'listUnordered')}
                    onKeyPress={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                            handleClick(e, 'listUnordered');
                        }
                    }}
                />
                <CustomIconButton
                    aria-label={'apply ordered list'}
                    active={editor?.isActive('orderedList')}
                    disabled={disabled}
                    kind={'listOrdered'}
                    size={'small'}
                    variant={'square'}
                    onMouseDown={(e) => handleClick(e, 'listOrdered')}
                    onKeyPress={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                            handleClick(e, 'listOrdered');
                        }
                    }}
                />
            </Stack>
            <Stack direction={'row'} spacing={1}>
                <CustomIconButton
                    aria-label={'apply link'}
                    disabled={disabled}
                    kind={'link'}
                    size={'small'}
                    variant={'square'}
                    onMouseDown={(e) => handleClick(e, 'link')}
                    onKeyPress={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                            handleClick(e, 'link');
                        }
                    }}
                />
            </Stack>
            <Stack direction={'row'} spacing={1}>
                <CustomIconButton
                    aria-label={'apply code formatting'}
                    active={editor?.isActive('code')}
                    disabled={disabled}
                    kind={'code'}
                    size={'small'}
                    variant={'square'}
                    onMouseDown={(e) => handleClick(e, 'code')}
                    onKeyPress={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                            handleClick(e, 'code');
                        }
                    }}
                />
            </Stack>
        </RootStack>
    );
};

const RootStack = styled(Stack)(({ disabled, theme }) => {
    return {
        margin: theme.spacing(-1),
        padding: theme.spacing(1),
        ...(disabled && {
            background: theme.palette.ensightful.neutral[300],
        }),
    };
});

const CustomIconButton = styled(IconButton)(({ active, theme }) => {
    return {
        '& > .MuiSvgIcon-root': {
            fontSize: '1rem',
        },
        ...(active && {
            background: theme.palette.ensightful.neutral[200],
            color: theme.palette.ensightful.neutral[900],
        }),
    };
});

export default RichTextToolbar;
